import React, { useState, useEffect, useMemo } from 'react';
import { useNotaire } from "../context/NotaireContext";
import { FolderIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import ModalAcceptationAffaire from '../components/ModalAcceptationAffaire';
import { FaceSmileIcon, HeartIcon, HomeIcon } from '@heroicons/react/24/solid';
import { Input } from "reactstrap";
import { CheckBadgeIcon, DocumentTextIcon, StarIcon, ViewfinderCircleIcon } from '@heroicons/react/24/outline';
import Pagination from "../components/Pagination";

export default function AllAffaires() {
  const context = useNotaire();
  //console.log("context in AllAffaires",context.user.google_maps_url_from_etudes);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [idAffaire, setIdAffaire] = useState("");
  const [affaires, setAffaires] = useState({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all"); // 'all' | 'immo' | ...
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const toggleSort = (key) =>
    setSort((prev) => {
      if (!key) return { key: 'createdAt', dir: 'desc' };
      return prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' };
    });

  const allTypes = [
    { key: "immo", label: "Immobilier", badgeClass: "badge-vendeur", fieldName: "nom", route: "immo"},
    { key: "pma", label: "PMA", badgeClass: "badge-acheteur", fieldName: "nom_from_user", route: "pma" },
    { key: "mariage", label: "Contrat de mariage", badgeClass: "badge-notaire", fieldName: "nom_from_user", route: "mariage" },
    { key: "pacs", label: "PACS", badgeClass: "badge-autre-notaire", fieldName: "nom_from_user", route: "pacs" },
    { key: "certification", label: "Certification", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "certification" },
    { key: "divorce", label: "Divorce", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "divorce" },
    { key: "bail", label: "Bail", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "bail" },
    { key: "donation", label: "Donation", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "donation" },
    { key: "succession", label: "Succession", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "succession" },
    { key: "testament", label: "Testament", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "testament" },
    { key: "statut-societe", label: "Statut de société", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "statut-societe" },
    { key: "adoption", label: "Adoption", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "adoption" },
    { key: "procuration", label: "Procuration", badgeClass: "badge-agent", fieldName: "nom_from_user", route: "procuration" },
  ];

  const openModalAcceptationAffaire = (id) => {
    setIdAffaire(id);
    setIsModalOpen(true);
  };

  function initList() {
    const temp = {
      mariage: context?.mariage || [],
      pacs: context?.pacs || [],
      pma: context?.pma || [],
      certification: context?.certification || [],
      immo: context?.immo || [],
      divorce: context?.divorce || [],
      bail: context?.bail || [],
      donation: context?.donation || [],
      succession: context?.succession || [],
      testament: context?.testament || [],
      adoption: context?.adoption || [],
      procuration: context?.procuration || [],
      "statut-societe": context?.["statut-societe"] || [], // ✅ FIX
      aucuneDemarche: context?.aucuneDemarche || [],
    };
    setAffaires(temp);
  }

  useEffect(() => {
    initList();
  }, [
    context?.mariage,
    context?.pacs,
    context?.pma,
    context?.immo,
    context?.aucuneDemarche,
    context?.certification,
    context?.divorce,
    context?.bail,
    context?.donation,
    context?.succession,
    context?.testament,
    context?.procuration,
    context?.adoption,
    context?.["statut-societe"],
  ]);

  // Filtre recherche
  const filteredAffaires = useMemo(() => {
    const lower = (search || "").toLowerCase().trim();
    const temp = {};
    for (const key of Object.keys(affaires || {})) {
      const list = affaires[key] || [];
      temp[key] = !lower
        ? list
        : list.filter((item) => {
            const f = item?.fields || {};
            const nomA = f.nom;
            const nomB = Array.isArray(f.nom_from_user) ? f.nom_from_user[0] : null;
            const value = (nomA ?? nomB ?? "").toString().toLowerCase();
            return value.includes(lower);
          });
    }
    return temp;
  }, [affaires, search]);

  // ✅ Compteurs visibles (hors "cloturée")
  const visibleCounts = useMemo(() => {
    const out = {};
    for (const t of allTypes) {
      const list = (filteredAffaires[t.key] || []).filter(
        (a) => a?.fields?.statut_marketplace_notaire !== "cloturée"
      );
      out[t.key] = list.length;
    }
    return out;
  }, [filteredAffaires, allTypes]); // ✅ deps

  const totalVisible = Object.values(visibleCounts).reduce((sum, n) => sum + n, 0);

  // ✅ plus fiable : toutes les catégories
  const noAffaires = allTypes.every((t) => !(context?.[t.key]?.length));

  // ---- helpers tri (IMPORTANT : date = nombre, pas string) ----
  const getTimeMs = (affaire) => {
    const raw =
      affaire?.fields?.date_creation ??
      affaire?.fields?.["date_création"]; // ✅ au cas où
    const ms = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(ms) ? ms : 0;
  };

  const getNameValue = (affaire, typeInfo) => {
    const v =
      affaire?.fields?.nom ??
      affaire?.fields?.nom_from_user?.[0] ??
      (typeInfo?.fieldName ? affaire?.fields?.[typeInfo.fieldName] : '') ??
      '';
    return String(v).toLowerCase();
  };

  const cmpString = (a, b, dir) => (dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a));
  const cmpNumber = (a, b, dir) => (dir === 'asc' ? a - b : b - a);

  // APLATIR + filtrer "cloturée" + trier
  const rows = useMemo(() => {
    const typesToShow =
      categoryFilter === "all"
        ? allTypes
        : allTypes.filter((t) => t.key === categoryFilter);

    const list = [];
    for (const t of typesToShow) {
      for (const a of filteredAffaires[t.key] || []) {
        if (a?.fields?.statut_marketplace_notaire === "cloturée") continue;
        list.push({ affaire: a, typeInfo: t });
      }
    }

    const key = sort?.key ?? 'createdAt';
    const dir = sort?.dir ?? 'desc';

    return [...list].sort((ra, rb) => {
      switch (key) {
        case 'createdAt':
          return cmpNumber(getTimeMs(ra.affaire), getTimeMs(rb.affaire), dir);

        case 'name':
          return cmpString(getNameValue(ra.affaire, ra.typeInfo), getNameValue(rb.affaire, rb.typeInfo), dir);

        case 'categorie':
          return cmpString(String(ra.typeInfo?.label ?? '').toLowerCase(), String(rb.typeInfo?.label ?? '').toLowerCase(), dir);

        case 'provenance':
          return cmpString(String(ra.affaire?.fields?.provenance ?? '').toLowerCase(), String(rb.affaire?.fields?.provenance ?? '').toLowerCase(), dir);

        case 'statut':
          return cmpString(String(ra.affaire?.fields?.statut_marketplace_notaire ?? '').toLowerCase(), String(rb.affaire?.fields?.statut_marketplace_notaire ?? '').toLowerCase(), dir);

        default:
          return 0;
      }
    });
  }, [filteredAffaires, categoryFilter, sort, allTypes]);

  // Reset page quand la liste change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, sort]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage]);

  const handleClickTag = (key) => {
    setCategoryFilter((prev) => (prev === key ? "all" : key));
  };

  const Tag = ({ k, label }) => {
    const active = categoryFilter === k;
    const count = visibleCounts[k] || 0;
    if (count === 0) return null;

    return (
      <button
        type="button"
        onClick={() => handleClickTag(k)}
        className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition
          ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        title={label}
      >
        {label}
        <span
          className={`ml-2 inline-flex items-center justify-center rounded-full text-xs ${
            active ? 'bg-white/20' : 'bg-gray-100'
          } px-2 py-0.5`}
        >
          {count}
        </span>
      </button>
    );
  };

  const formatNoms = (val) => {
    if (!val) return null;
    const arr = Array.isArray(val) ? val : [val];
    const parts = arr
      .flatMap(item => typeof item === 'string' ? item.split(',') : [])
      .map(s => s.trim())
      .filter(Boolean);
    return parts.join(' - ');
  };

  return (
    <>
      {noAffaires ? (
        <div className="overflow-hidden rounded-lg bg-white shadow-sm mt-3 text-center">
          <EmptyState
            icon={FolderIcon}
            title="Aucune affaire en cours"
            description="Lorsqu'une affaire vous sera confiée, vous serez averti par email."
          />
        </div>
      ) : (
        <div className="">
          {/* Barre de recherche + tags */}
          <div className="mt-4 flex flex-col gap-3 sm:!flex-col sm:items-center sm:justify-between">
            <Input
              id="nom"
              name="nom"
              type="text"
              placeholder="Rechercher une affaire…"
              className="w-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategoryFilter("all")}
                className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition
                  ${categoryFilter === 'all'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                title="Toutes catégories"
              >
                Tous
                <span
                  className={`ml-2 inline-flex items-center justify-center rounded-full text-xs ${
                    categoryFilter === 'all' ? 'bg-white/20' : 'bg-gray-100'
                  } px-2 py-0.5`}
                >
                  {totalVisible}
                </span>
              </button>

              {allTypes.map(t => (
                <Tag key={t.key} k={t.key} label={`${t.label}`} />
              ))}
            </div>
          </div>

          <div className="mt-8 flow-root">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 cursor-pointer select-none"
                        onClick={() => toggleSort('name')}
                      >
                        Nom {sort.key === 'name' ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none"
                        onClick={() => toggleSort('categorie')}
                      >
                        Catégorie {sort.key === 'categorie' ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none"
                        onClick={() => toggleSort('provenance')}
                      >
                        Provenance {sort.key === 'provenance' ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none"
                        onClick={() => toggleSort('createdAt')}
                        title="Trier par date de création"
                      >
                        Date de création {sort.key === 'createdAt' ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </th>

                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                        <span className="sr-only">#</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedRows.map(({ affaire, typeInfo }) => {
                      const Icon = typeInfo.icon;

                      const rawDate =
                        affaire?.fields?.date_creation ??
                        affaire?.fields?.["date_création"];

                      return (
                        <tr key={affaire.id}>
                          <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-0 items-center">
                            <div className="flex items-center">
                              <div className="font-bold text-gray-900 ml-2">
                                {formatNoms(affaire.fields.nom_from_user) ?? affaire.fields.nom}
                                {" "}
                                {formatNoms(affaire.fields.prenom_from_user) ?? affaire.fields.prenom}
                              </div>
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <div className={typeInfo.badgeClass}>
                              {Icon && <Icon className="size-3" />}
                              {typeInfo.label}
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            {affaire.fields.provenance === "interne" && <>
                            <span className="inline-flex items-center rounded-md  px-2 py-1 text-xs font-bold">
                                Interne
                              </span>
                            </>
                            }
                            {affaire.fields.provenance === "clotere" && <>
                            <span className="inline-flex items-center rounded-md  px-2 py-1 text-xs font-bold">
                                Clotere
                              </span>
                            </>
                            }
                            {affaire.fields.provenance === "partenaire" && <>
                            <span className="inline-flex items-center rounded-md  px-2 py-1 text-xs font-bold">
                                Partenaire
                              </span>
                            </>
                            }
                            {affaire.fields.provenance === "widget" && <>
                            <span className="inline-flex items-center rounded-md  px-2 py-1 text-xs font-bold">
                                Widget
                              </span>
                            </>
                            }
                          </td>

                          <td className="whitespace-nowrap px-3 py-3  text-gray-500">
                            <small className='text-gray-400'>
                              {rawDate
                                ? new Date(rawDate).toLocaleString("fr-FR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "—"}
                            </small>
                          </td>

                          <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-center text-sm font-medium sm:pr-0">
                            {affaire.fields.statut_marketplace_notaire === "vérification avant attribution" ? (
                              <>
                                <Link
                                  className="btn-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openModalAcceptationAffaire(affaire.id);
                                  }}
                                >
                                  Prendre en charge
                                </Link>
                                {isModalOpen && (
                                  <ModalAcceptationAffaire
                                    type={typeInfo.route}
                                    idAffaire={affaire.id}
                                    onClose={() => setIsModalOpen(false)}
                                  />
                                )}
                              </>
                            ) : (
                              <Link className="btn-main no-underline" to={`/app/affaire/${typeInfo.route}/${affaire.id}`}>
                                Voir
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}